import React from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';

import Modal from '../common/Modal';
import { GateEntry } from '../../types';
import { grnService } from '../../services/grnService';
import { dispatchService } from '../../services/dispatchService';
import { formatDateTime } from '../../utils/dateTime';

interface PrintItem {
  id: number;
  productDescription: string;
  itemCode?: string;
  skuCode?: string;
  batchNo?: string;
  goodQty: number;
  cdamQty: number;
  damQty: number;
  palletCount: number;
  qtyPerPallet?: number | null;
  // GRN items: resolved by the backend (grn.service.ts enhanceStorageDetails/
  // enhanceLbdStorageDetails) into zoneCode/zoneLocationCode/rackCode/locationCode.
  storageDetails?: Array<Record<string, any>>;
  lbdStorageDetails?: Array<Record<string, any>>;
  // Dispatch items: resolved by dispatch.service.ts findItemsByDispatch into
  // zone/zoneLocation/rack/location + palletsToDispatch.
  storageSelections?: Array<Record<string, any>>;
}

interface GateEntryPrintViewProps {
  entry: GateEntry;
  onClose: () => void;
}

type LocationType = 'Zone' | 'Rack';

interface PlacementRow {
  key: string;
  location: string;
  locationType: LocationType | null;
  productDescription: string;
  batchNo?: string;
  qty: number | null;
  pallets: number;
  isDamaged: boolean;
}

interface ResolvedLocation {
  label: string;
  type: LocationType;
}

// Just the location text (+ whether it's ground-storage Zone or rack Rack) —
// no pallet count baked in — so it can be paired with an exact per-location
// quantity in the flattened placement table below. Duck-types on whichever
// fields are populated rather than trusting storageType, same approach as
// PastGrnUpdateModals.tsx's locationChips, since it works even when
// storageType is missing on legacy items.
const grnLocation = (d: Record<string, any> | null | undefined): ResolvedLocation | null => {
  if (!d || typeof d !== 'object') return null;
  if (d.zoneCode || d.zoneId != null) {
    const label = d.zoneLocationCode || d.zoneCode || `Zone ${d.zoneId}`;
    return { label, type: 'Zone' };
  }
  if (d.rackCode || d.locationCode || d.rackId != null || d.locationId != null) {
    const rack = d.rackCode || (d.rackId != null ? `Rack ${d.rackId}` : null);
    const loc = d.locationCode || (d.locationId != null ? `Loc ${d.locationId}` : null);
    const label = [rack, loc].filter(Boolean).join(' / ');
    return label ? { label, type: 'Rack' } : null;
  }
  return null;
};

const dispatchLocation = (s: Record<string, any> | null | undefined): ResolvedLocation | null => {
  if (!s) return null;
  if (s.zoneLocation || s.zone) {
    return { label: s.zoneLocation || s.zone, type: 'Zone' };
  }
  const label = [s.rack, s.location].filter(Boolean).join(' / ');
  return label ? { label, type: 'Rack' } : null;
};

// Splits a total quantity across a sequence of per-location pallet counts:
// full pallets get qtyPerPallet each, the last short pallet gets whatever's
// left — the same distribution rule inventory-storage.service.ts uses when it
// assigns rack locations after approval. Without qtyPerPallet there's no
// reliable way to split the total, so the first entry absorbs it all rather
// than guessing.
const distributeQty = (
  palletCounts: number[],
  qtyPerPallet: number | null | undefined,
  total: number,
): number[] => {
  let remaining = total;
  return palletCounts.map((pallets) => {
    const qty =
      qtyPerPallet && qtyPerPallet > 0
        ? Math.min(pallets * qtyPerPallet, Math.max(remaining, 0))
        : remaining;
    remaining -= qty;
    return qty;
  });
};

// Explodes one GRN/dispatch item into one row per physical location it's
// assigned to — this is what an operator doing putaway/picking actually needs:
// not "this product has 2 pallets somewhere", but "put 12 units / 1 pallet at
// AA-04, and 8 units / 1 pallet at AK-02".
const buildPlacementRows = (item: PrintItem, isInward: boolean): PlacementRow[] => {
  const rows: PlacementRow[] = [];

  if (isInward) {
    const good = (item.storageDetails ?? []).filter((d) => d && typeof d === 'object');
    // Rack entries carry no explicit `pallets` field — each one IS one pallet
    // at one location (only zone entries group several pallets under one
    // shared zone and always set `pallets` explicitly) — so default to 1
    // rather than 0.
    const goodPallets = good.map((d) => Number(d.pallets) || 1);
    const goodQtys = distributeQty(goodPallets, item.qtyPerPallet, item.goodQty);
    good.forEach((d, i) => {
      const resolved = grnLocation(d);
      if (!resolved) return;
      rows.push({
        key: `${item.id}-good-${i}`,
        location: resolved.label,
        locationType: resolved.type,
        productDescription: item.productDescription,
        batchNo: item.batchNo,
        qty: goodQtys[i],
        pallets: goodPallets[i],
        isDamaged: false,
      });
    });

    const lbd = (item.lbdStorageDetails ?? []).filter((d) => d && typeof d === 'object');
    const lbdPallets = lbd.map((d) => Number(d.pallets) || 1);
    const lbdQtys = distributeQty(lbdPallets, item.qtyPerPallet, (item.cdamQty || 0) + (item.damQty || 0));
    lbd.forEach((d, i) => {
      const resolved = grnLocation(d);
      if (!resolved) return;
      rows.push({
        key: `${item.id}-lbd-${i}`,
        location: `LBD · ${resolved.label}`,
        locationType: resolved.type,
        productDescription: item.productDescription,
        batchNo: item.batchNo,
        qty: lbdQtys[i],
        pallets: lbdPallets[i],
        isDamaged: true,
      });
    });
  } else {
    // quantityToDispatch is the actual units picked from this location —
    // separate from palletsToDispatch, which only tracks whether a whole
    // pallet physically leaves (dispatch-item.entity.ts). A "shared pallet,
    // qty only" pick legitimately has palletsToDispatch: 0 with a real
    // quantityToDispatch, so 0 pallets here is meaningful, not missing data.
    (item.storageSelections ?? []).forEach((s, i) => {
      const resolved = dispatchLocation(s);
      if (!resolved) return;
      rows.push({
        key: `${item.id}-pick-${i}`,
        location: resolved.label,
        locationType: resolved.type,
        productDescription: item.productDescription,
        batchNo: item.batchNo,
        qty: s.quantityToDispatch ?? null,
        pallets: Number(s.palletsToDispatch) || 0,
        isDamaged: false,
      });
    });
  }

  // No location decided yet (still mid-creation) — surface the item anyway so
  // nothing silently disappears from the sheet.
  if (rows.length === 0) {
    rows.push({
      key: `${item.id}-unassigned`,
      location: '(location not yet assigned)',
      locationType: null,
      productDescription: item.productDescription,
      batchNo: item.batchNo,
      qty: isInward ? item.goodQty : null,
      pallets: item.palletCount,
      isDamaged: false,
    });
  }

  return rows;
};

// Header fields present on every gate entry regardless of GRN/dispatch status —
// shown even for a bare draft with no GRN/dispatch created yet.
const HEADER_FIELDS = (entry: GateEntry): Array<[string, string | number | null | undefined]> => [
  ['Entry Date', entry.entryDate ? formatDateTime(entry.entryDate) : entry.entryTime],
  ['Vehicle Number', entry.vehicleNumber],
  ['Driver Name', entry.driverName],
  ['Driver Phone', entry.driverPhone],
  ['Client', entry.clientName],
  ['Dock Number', entry.dockNumber],
  ['Invoice Number', entry.invoiceNumber],
  ['E-way Bill Number', entry.ewayBillNumber],
  ['Packing Slip Number', entry.packingSlipNumber],
  ['Expected Quantity', entry.expectedQuantity],
  ['Remarks', entry.remarks],
];

const PrintableContent: React.FC<{
  entry: GateEntry;
  items: PrintItem[] | undefined;
  itemsLoading: boolean;
}> = ({ entry, items, itemsLoading }) => {
  const isInward = entry.shipmentType === 'inward';
  const docNumber = entry.entryNumber ?? entry.gateEntryNumber;
  const refNumber = isInward ? entry.grnNumber : entry.dispatchNumber;

  const placementRows = (items ?? [])
    .flatMap((item) => buildPlacementRows(item, isInward))
    .sort((a, b) => a.location.localeCompare(b.location));

  return (
    <div className="text-sm text-gray-900">
      <h2 className="text-lg font-semibold mb-1">
        {isInward ? 'Inbound Entry' : 'Outbound Entry'} — {docNumber}
      </h2>
      {refNumber && (
        <p className="text-gray-600 mb-4">{isInward ? 'GRN' : 'Dispatch'}: {refNumber}</p>
      )}

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 mb-6">
        {HEADER_FIELDS(entry).map(([label, value]) => (
          <div key={label} className="flex justify-between border-b border-gray-100 py-1">
            <span className="text-gray-500">{label}</span>
            <span className="font-medium text-right">{value || '-'}</span>
          </div>
        ))}
      </div>

      {itemsLoading && <p className="text-gray-500">Loading item details…</p>}

      {!itemsLoading && placementRows.length > 0 && (
        <>
          <h3 className="font-semibold mb-2">
            {isInward ? 'Putaway — where to store each product' : 'Pick list — where to pick each product from'}
          </h3>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-300">
                <th className="py-1 pr-2">Location</th>
                <th className="py-1 pr-2">Type</th>
                <th className="py-1 pr-2">Product</th>
                <th className="py-1 pr-2">Batch No</th>
                <th className="py-1 pr-2 text-right">Qty</th>
                <th className="py-1 text-right">Pallets</th>
              </tr>
            </thead>
            <tbody>
              {placementRows.map((row) => (
                <tr key={row.key} className="border-b border-gray-100">
                  <td className={`py-1 pr-2 font-medium ${row.isDamaged ? 'text-amber-700' : ''}`}>
                    {row.location}
                  </td>
                  <td className="py-1 pr-2">
                    {row.locationType ? (
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                          row.locationType === 'Zone'
                            ? 'bg-brand-50 text-brand-700'
                            : 'bg-purple-50 text-purple-700'
                        }`}
                      >
                        {row.locationType}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-1 pr-2">{row.productDescription}</td>
                  <td className="py-1 pr-2">{row.batchNo || '-'}</td>
                  <td className="py-1 pr-2 text-right">{row.qty ?? '-'}</td>
                  <td className="py-1 text-right">{row.pallets}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

// Prints a gate entry's details as a putaway/pick slip (header always,
// location-wise product/quantity lines once a GRN/dispatch exists) so an
// operator can be handed a physical copy telling them exactly where to store
// or pick each product. The printable markup is duplicated into a portal
// outside #root, with its own inline <style> controlling visibility — kept
// self-contained (not relying on a global stylesheet or Tailwind's `print:`
// variant surviving the build) because content nested inside this Modal's
// scrollable/fixed ancestors gets clipped or fails to paginate when printed
// directly.
const GateEntryPrintView: React.FC<GateEntryPrintViewProps> = ({ entry, onClose }) => {
  const isInward = entry.shipmentType === 'inward';

  const { data: grnItems, isLoading: grnItemsLoading } = useQuery({
    queryKey: ['grn', entry.grnId, 'print-items'],
    queryFn: () => grnService.getItems(entry.grnId as number),
    enabled: isInward && !!entry.grnId,
  });

  const { data: dispatchItems, isLoading: dispatchItemsLoading } = useQuery({
    queryKey: ['dispatch', entry.dispatchId, 'print-items'],
    queryFn: () => dispatchService.getDispatchItems(entry.dispatchId as number),
    enabled: !isInward && !!entry.dispatchId,
  });

  const items: PrintItem[] | undefined = isInward ? grnItems : dispatchItems;
  const itemsLoading = isInward ? !!entry.grnId && grnItemsLoading : !!entry.dispatchId && dispatchItemsLoading;

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title="Print Preview"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
            >
              Close
            </button>
            <button
              onClick={() => window.print()}
              className="px-4 py-2 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 flex items-center gap-1.5"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
        }
      >
        <PrintableContent entry={entry} items={items} itemsLoading={itemsLoading} />
      </Modal>
      {createPortal(
        <div id="gate-entry-print-area">
          <style>{`
            #gate-entry-print-area { display: none; }
            @media print {
              /* Hide every other direct child of <body> — #root, and also
                 Modal's own on-screen preview, which portals straight into
                 document.body (bypassing #root entirely) and would otherwise
                 print its backdrop/panel on top of this content. */
              body > *:not(#gate-entry-print-area) { display: none !important; }
              #gate-entry-print-area { display: block !important; padding: 2rem; }
            }
          `}</style>
          <PrintableContent entry={entry} items={items} itemsLoading={itemsLoading} />
        </div>,
        document.body,
      )}
    </>
  );
};

export default GateEntryPrintView;
